import { defineNuxtPlugin, useRhythmicLogger, useRouter } from '#imports'

/**
 * Platform-level fallback for syncing kanban board route params into `useKanbanBoardContext`.
 *
 * This guards against cases where the module-provided plugin is not picked up due to
 * stale `.nuxt` generation; it no-ops if the module plugin already installed itself.
 */
export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  type RhythmicKanbanBoardRouterGlobal = typeof globalThis & {
    __rhythmicKanbanBoardContextRouterInstalled?: boolean
  }

  const globalState = globalThis as RhythmicKanbanBoardRouterGlobal
  if (globalState.__rhythmicKanbanBoardContextRouterInstalled) return

  const logger = useRhythmicLogger('platformKanbanBoardContextRouterPlugin')
  const router = useRouter()
  const kanbanBoardContext = useKanbanBoardContext()

  /**
   * Normalizes a route param into a stable `string | undefined` for the kanban board context.
   */
  function normalizeParam(value: unknown): string | undefined {
    const resolvedValue = Array.isArray(value) ? value[0] : value

    if (typeof resolvedValue !== 'string') return undefined

    const trimmed = resolvedValue.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }

  /**
   * Applies the current route params to the kanban board context and emits change events.
   */
  function syncRoute(to: { fullPath: string; params: Record<string, unknown> }) {
    const nextHarmonyPlanId = normalizeParam(to.params.id)
    const nextBoardId = normalizeParam(to.params.boardId)

    const previousHarmonyPlanId = kanbanBoardContext.harmonyPlanId
    const previousBoardId = kanbanBoardContext.boardId

    const planChanged = previousHarmonyPlanId !== nextHarmonyPlanId
    const boardChanged = previousBoardId !== nextBoardId

    if (!planChanged && !boardChanged) return

    kanbanBoardContext.harmonyPlanId = nextHarmonyPlanId
    kanbanBoardContext.boardId = nextBoardId

    if (planChanged) {
      void kanbanBoardContext.queries.emitPlanIdChanged({
        path: to.fullPath,
        harmonyPlanId: nextHarmonyPlanId,
        previousHarmonyPlanId,
      })
    }

    if (boardChanged) {
      void kanbanBoardContext.queries.emitBoardIdChanged({
        path: to.fullPath,
        boardId: nextBoardId,
        previousBoardId,
      })
    }
  }

  syncRoute(router.currentRoute.value)

  const removeAfterEach = router.afterEach((to) => {
    syncRoute(to)
  })

  void router
    .isReady()
    .then(() => syncRoute(router.currentRoute.value))
    .catch((error) => {
      logger.error('Failed to sync initial route for kanban board context', {
        error,
      })
    })

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      removeAfterEach()
    })
  }
})
